import moment from "moment";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { useState } from "react";

import Calendar, { CalendarProps } from "../calendar";
import { Footer } from "./footer";
import { isDateBetween } from "./helpers";
import { Option, Options } from "./options";

type RangeCalendarProps = {
  startDate: string | null;
  endDate: string | null;
  minDate?: string;
  displayMonth?: string | null;
  options?: Option[];
  showFooterInfo?: boolean;
  handleChange: (startDate: string, endDate: string, optionId?: string) => void;
  checkHighlighted?: (date: string, range: { startDate: string | null; endDate: string | null }) => boolean;
  onStartDateChange?: (startDate: string) => void;
  portalRef?: React.RefObject<HTMLDivElement>;
  maxDaysLength?: number;
} & CalendarProps;

export const RangeCalendar = ({
  startDate: initStartDate,
  endDate: initEndDate,
  minDate,
  displayMonth,
  options,
  showFooterInfo = true,
  handleChange,
  checkHighlighted,
  onStartDateChange,
  portalRef,
  maxDaysLength,
  ...calendarProps
}: RangeCalendarProps) => {
  const { formatISODate } = useDateFormatter();
  const [startDate, setStartDate] = useState(initStartDate);
  const [endDate, setEndDate] = useState(initEndDate);
  const [hoverEndDay, setHoverEndDay] = useState(null);
  const [lastHoverEndDay, setLastHoverEndDay] = useState(displayMonth);

  const checkHover = (date: string) => {
    if (startDate && !endDate && hoverEndDay) {
      return isDateBetween(date, startDate, hoverEndDay);
    }
    return false;
  };

  const checkSelected = (date: string) => {
    if (startDate && endDate) {
      return isDateBetween(date, startDate, endDate);
    }
    return false;
  };

  const handleCheckHighlighted = (date: string) => {
    if (checkHighlighted) {
      return checkHighlighted(date, { startDate, endDate });
    } else if (startDate && endDate) {
      return isDateBetween(date, startDate, endDate);
    }
    return false;
  };

  const handleDateClick = (date) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(formatISODate(moment(date).startOf("day")));
      setEndDate(null);
      setLastHoverEndDay(null);
      onStartDateChange && onStartDateChange(formatISODate(moment(date).startOf("day")));
    } else {
      const newEndDate = formatISODate(moment(date).startOf("day"));
      setEndDate(newEndDate);
      setLastHoverEndDay(null);
      handleChange && handleChange(formatISODate(startDate), formatISODate(newEndDate));
    }
  };

  const handleMouseEnter = (day) => {
    setLastHoverEndDay(day);
    setHoverEndDay(day);
  };

  const handleMouseLeave = () => setHoverEndDay(null);

  const handleReset = () => {
    setStartDate(initStartDate);
    setEndDate(initEndDate);
  };

  const handleOptionSelect = (option) =>
    handleChange(formatISODate(moment(option.item.start)), formatISODate(moment(option.item.end)), option.id);

  return (
    <Calendar
      maxDate={
        startDate && maxDaysLength !== undefined
          ? moment(startDate)
              .add(maxDaysLength - 1, "days")
              .toISOString()
          : undefined
      }
      {...calendarProps}
      displayMonthValue={formatISODate(lastHoverEndDay || endDate || startDate || displayMonth)}
      minDate={startDate && !endDate ? startDate : minDate}
      checkHighlighted={handleCheckHighlighted}
      checkHover={checkHover}
      checkSelected={checkSelected}
      handleMouseEnter={handleMouseEnter}
      handleMouseLeave={handleMouseLeave}
      showTime={false}
      handleChange={handleDateClick}
      sidePanel={options && <Options options={options} onSelect={handleOptionSelect} />}
      portalRef={portalRef}
    >
      {showFooterInfo ? (
        <Footer
          startDate={startDate ? formatISODate(startDate) : null}
          endDate={endDate ? formatISODate(endDate) : null}
          onReset={handleReset}
          isRegularText={!!options}
          maxDaysLength={maxDaysLength}
        />
      ) : null}
    </Calendar>
  );
};
